import { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Badge, Spinner, Row, Col, Table } from 'react-bootstrap';
import { FaTimesCircle, FaEdit, FaBuilding, FaChartLine, FaExclamationTriangle, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { toast } from 'sonner';
import { getSecurityById, getSecurityPriceHistory } from '../../../service/api/SecurityService';
import { formatCurrencys } from '../../../service/utils/facturationUtils';
import { formatDate } from '../../../service/utils/formatters';
import '../style/navbar.css';
import PriceEvolutionChart from './PriceHistoryChart';

const SecurityDetailsModal = ({ 
  show, 
  onHide, 
  securityId,
  role, 
  onDelete, 
  onEditTitle
}) => {
  const [securityDetails, setSecurityDetails] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (show && securityId) {
      fetchSecurityDetails();
      fetchPriceHistory();
    } else {
      setSecurityDetails(null);
      setPriceHistory([]);
      setActiveTab('details');
    }
  }, [show, securityId]);

  const fetchSecurityDetails = async () => {
    setLoading(true);
    try {
      const details = await getSecurityById(securityId);
      setSecurityDetails(details.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails du titre');
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceHistory = async () => {
    setLoadingHistory(true);
    try {
      const history = await getSecurityPriceHistory(securityId);
      setPriceHistory(history || []);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'historique des prix');
      setPriceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleEditTitle = () => {
    if (securityDetails && onEditTitle) {
      onEditTitle(securityDetails);
    }
  };

  const handleDelete = () => {
    if (securityDetails && onDelete) {
      onDelete(securityDetails.id, securityDetails.name);
    }
  };

  // Préparer les données d'historique avec gestion intelligente du volume
  const processedHistory = useMemo(() => {
    if (!priceHistory || priceHistory.length === 0) return [];

    // Trier par date décroissante (du plus récent au plus ancien)
    const sorted = [...priceHistory].sort((a, b) => 
      new Date(b.datetime || b.lastUpdateDate) - new Date(a.datetime || a.lastUpdateDate)
    );

    // Traiter les données avec gestion du volume
    return sorted.map((item, index) => {
      const previousItem = index < sorted.length - 1 ? sorted[index + 1] : null;
      const previousPrice = previousItem?.adjustedPrice || item.price;
      const currentPrice = item.adjustedPrice || item.price;
      
      // Si le volume n'a pas changé, utiliser l'ancien volume
      const volume = item.volume !== undefined && item.volume !== null 
        ? item.volume 
        : previousItem?.volume || 0;

      const variation = previousPrice && previousPrice !== 0
        ? {
            amount: currentPrice - previousPrice,
            percent: ((currentPrice - previousPrice) / previousPrice) * 100
          }
        : { amount: 0, percent: 0 };

      return {
        ...item,
        currentPrice,
        previousPrice,
        volume,
        variation,
        isIncrease: variation.amount >= 0
      };
    });
  }, [priceHistory]);

  // Statistiques de l'historique
  const historyStats = useMemo(() => {
    if (processedHistory.length === 0) return null;

    const prices = processedHistory.map(h => h.currentPrice);
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    const totalVolume = processedHistory.reduce((sum, h) => sum + (h.volume || 0), 0);

    return { maxPrice, minPrice, avgPrice, totalVolume, count: processedHistory.length };
  }, [processedHistory]);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="security-details-modal">
      <Modal.Header closeButton className="bg-light border-bottom">
        <div className="d-flex align-items-center w-100">
          <FaBuilding className="text-primary me-3" size={24} />
          <div className="flex-grow-1">
            <Modal.Title className="h4 mb-1">
              {loading ? 'Chargement...' : securityDetails?.name || 'Détails du Titre'}
            </Modal.Title>
            {securityDetails && (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Badge bg="primary" className="bg-opacity-10 text-primary border border-primary border-opacity-25">
                  {securityDetails.symbol || "N/A"}
                </Badge>
                <Badge bg="secondary" className="bg-opacity-10 text-secondary border border-secondary border-opacity-25">
                  {securityDetails.isin || "N/A"}
                </Badge>
                <Badge 
                  bg={securityDetails.repayable ? "success" : "warning"} 
                  className="bg-opacity-10 border border-opacity-25"
                  style={{ color: securityDetails.repayable ? '#198754' : '#ffc107' }}
                >
                  {securityDetails.repayable ? "Remboursable" : "Non remboursable"}
                </Badge>
              </div>
            )}
          </div>
        </div>
      </Modal.Header>

      {/* Navigation par onglets */}
      <div className="border-bottom bg-light">
        <div className="container-fluid px-4">
          <nav className="nav nav-tabs border-0">
            <button
              className={`nav-link px-4 py-3 border-0 ${activeTab === 'details' ? 'active bg-white text-primary fw-semibold' : 'text-muted bg-transparent'}`}
              onClick={() => setActiveTab('details')}
            >
              <FaBuilding className="me-2" />
              Informations détaillées
            </button>
            <button
              className={`nav-link px-4 py-3 border-0 ${activeTab === 'history' ? 'active bg-white text-primary fw-semibold' : 'text-muted bg-transparent'}`}
              onClick={() => setActiveTab('history')}
            >
              <FaChartLine className="me-2" />
              Historique des prix
              {processedHistory.length > 0 && (
                <Badge bg="primary" className="ms-2" pill>
                  {processedHistory.length}
                </Badge>
              )}
            </button>
          </nav>
        </div>
      </div>

      <Modal.Body className="p-4" style={{ minHeight: '400px', maxHeight: '70vh', overflowY: 'auto' }}>
        {loading && activeTab === 'details' ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Chargement des détails...</p>
          </div>
        ) : activeTab === 'details' ? (
          securityDetails ? (
            <div className="security-details">
              <Row className="g-4">
                <Col md={6}>
                  <div className="card border-0 bg-light h-100">
                    <div className="card-header bg-transparent border-bottom">
                      <h6 className="mb-0 fw-semibold">📋 Informations Générales</h6>
                    </div>
                    <div className="card-body">
                      <DetailItem label="Type" value={securityDetails.type} />
                      <DetailItem label="Marché cible" value={securityDetails.targetMarket} />
                      <DetailItem label="Fréquence coupon" value={securityDetails.couponFrequency} />
                      <DetailItem label="Taux coupon" value={`${securityDetails.couponRate || 0}%`} />
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="card border-0 bg-light h-100">
                    <div className="card-header bg-transparent border-bottom">
                      <h6 className="mb-0 fw-semibold">💰 Informations Financières</h6>
                    </div>
                    <div className="card-body">
                      <DetailItem 
                        label="Prix d'émission" 
                        value={formatCurrencys(securityDetails.issuePrice)} 
                      />
                      <DetailItem 
                        label="Dernier prix" 
                        value={formatCurrencys(securityDetails.lastPrice)} 
                      />
                      <DetailItem 
                        label="Unités Max émises" 
                        value={securityDetails.totalUnitsIssue?.toLocaleString() || "N/A"} 
                      />
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="card border-0 bg-light h-100">
                    <div className="card-header bg-transparent border-bottom">
                      <h6 className="mb-0 fw-semibold">📅 Dates Importantes</h6>
                    </div>
                    <div className="card-body">
                      <DetailItem label="Date de création" value={formatDate(securityDetails.creationDate)} />
                      <DetailItem label="Date d'émission" value={formatDate(securityDetails.issueDate)} />
                      <DetailItem label="Date de Jouissance" value={formatDate(securityDetails.dateJouissance)} />
                      <DetailItem label="Dernière Modification" value={formatDate(securityDetails.lastUpdateDate)} />
                    </div>
                  </div>
                </Col>

                <Col md={6}>
                  <div className="card border-0 bg-light h-100">
                    <div className="card-header bg-transparent border-bottom">
                      <h6 className="mb-0 fw-semibold">🏢 Émetteur</h6>
                    </div>
                    <div className="card-body">
                      <DetailItem 
                        label="Compte émetteur" 
                        value={securityDetails.issuerAccountName?.trim() || "N/A"} 
                      />
                      <DetailItem label="Entreprise" value={securityDetails.issuerAccountName || "N/A"} />
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <FaExclamationTriangle className="mb-3 opacity-50" size={48} />
              <h5 className="text-muted">Aucune donnée disponible</h5>
              <p className="mb-0">Impossible de charger les détails du titre</p>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={fetchSecurityDetails}
                className="mt-3"
              >
                <FaEdit className="me-2" />
                Réessayer
              </Button>
            </div>
          )
        ) : (
          /* ONGLET HISTORIQUE DES PRIX */
          <div className="price-history-tab">
            {loadingHistory ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="info" />
                <p className="mt-3 text-muted">Chargement de historique...</p>
              </div>
            ) : processedHistory.length > 0 ? (
              <>
                {/* Statistiques globales */}
                {historyStats && (
                  <Row className="g-3 mb-4">
                    <Col md={3}>
                      <div className="card border-0 bg-light text-center py-3">
                        <div className="text-muted small mb-1">Prix Maximum</div>
                        <div className="h5 mb-0 text-success fw-bold">
                          {formatCurrencys(historyStats.maxPrice)}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="card border-0 bg-light text-center py-3">
                        <div className="text-muted small mb-1">Prix Minimum</div>
                        <div className="h5 mb-0 text-danger fw-bold">
                          {formatCurrencys(historyStats.minPrice)}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="card border-0 bg-light text-center py-3">
                        <div className="text-muted small mb-1">Prix Moyen</div>
                        <div className="h5 mb-0 text-primary fw-bold">
                          {formatCurrencys(historyStats.avgPrice)}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="card border-0 bg-light text-center py-3">
                        <div className="text-muted small mb-1">Modifications</div>
                        <div className="h5 mb-0 text-info fw-bold">
                          {historyStats.count}
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Graphique d'évolution */}
                <div className="mb-4">
                  <PriceEvolutionChart priceHistory={priceHistory} />
                </div>

                {/* Tableau détaillé */}
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-light border-bottom">
                    <h6 className="mb-0 fw-semibold">
                      <FaChartLine className="me-2 text-primary" />
                      Détails des modifications
                    </h6>
                  </div>
                  <div className="table-responsive">
                    <Table hover className="mb-0 table-striped">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4">Date</th>
                          <th className="text-end">Prix précédent</th>
                          <th className="text-end">Nouveau prix</th>
                          <th className="text-center">Variation</th>
                          <th className="text-center">Volume</th>
                        </tr>
                      </thead>
                      <tbody>
                        {processedHistory.map((item, index) => (
                          <tr key={index} className={index === 0 ? 'table-active' : ''}>
                            <td className="ps-4">
                              <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 rounded p-2 me-3">
                                  <FaChartLine className="text-primary" size={14} />
                                </div>
                                <div>
                                  <div className="fw-semibold">{formatDate(item.datetime || item.lastUpdateDate)}</div>
                                  {index === 0 && (
                                    <Badge bg="success" className="badge-sm">Plus récent</Badge>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="text-end">
                              <span className="text-muted">
                                {formatCurrencys(item.previousPrice)}
                              </span>
                            </td>
                            <td className="text-end">
                              <span className="fw-bold text-dark">
                                {formatCurrencys(item.currentPrice)}
                              </span>
                            </td>
                            <td className="text-center">
                              <Badge 
                                bg={item.isIncrease ? "success" : "danger"}
                                className="bg-opacity-10 border border-opacity-25 d-inline-flex align-items-center gap-1"
                                style={{ color: item.isIncrease ? '#198754' : '#dc3545' }}
                              >
                                {item.isIncrease ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                                {item.isIncrease ? '+' : ''}{item.variation.percent.toFixed(2)}%
                              </Badge>
                            </td>
                            <td className="text-center">
                              <Badge bg="secondary" className="bg-opacity-10 text-secondary border border-secondary border-opacity-25">
                                {item.volume?.toLocaleString() || '0'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-5">
                <FaExclamationTriangle className="text-muted mb-3" size={48} />
                <h5 className="text-muted mb-3">Aucun historique de prix disponible</h5>
                <p className="text-muted mb-4">
                  Ce titre n'a subi aucune modification de prix pour le moment.
                </p>
                <div className="bg-light rounded p-3 mx-auto" style={{ maxWidth: '400px' }}>
                  <small className="text-muted">
                    <strong>💡 Information :</strong> L'historique des prix apparaîtra ici 
                    après chaque modification du prix du titre.
                  </small>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light border-top">
        <div className="d-flex justify-content-between w-100 flex-wrap gap-2">
          <div className="d-flex gap-2 flex-wrap">
            {(role === "admin+manager" || role === "admin") && securityDetails && (
              <>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleDelete}
                  className="d-flex align-items-center"
                >
                  <FaTimesCircle className="me-1" />
                  Supprimer
                </Button>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={handleEditTitle}
                  className="d-flex align-items-center"
                >
                  <FaEdit className="me-1" />
                  Modifier
                </Button>
              </>
            )}
          </div>
          <Button variant="secondary" onClick={onHide}>
            Fermer
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const DetailItem = ({ label, value }) => (
  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
    <span className="text-black small fw-bold">{label}:</span>
    <span className="badge bg-light bg-opacity-10 border border-opacity-25 text-primary" style={{ maxWidth: '60%' }}>
      {value || "N/A"}
    </span>
  </div>
);

export default SecurityDetailsModal;